import React from 'react';
import { Monitor, DollarSign, Users, Settings, Trophy, Zap, ClipboardList } from 'lucide-react';

const DashboardSection = ({
    title,
    subtitle,
    icon,
    gradient = false,
    actions = [],
    children
}) => (
    <section className={`dashboard-section${gradient ? ' gradient' : ''}`}>
        <div className="dashboard-section-header">
            <div className="dashboard-section-heading">
                {icon && <span className="dashboard-section-icon">{icon}</span>}
                <div>
                    <h2>{title}</h2>
                    {subtitle && <p>{subtitle}</p>}
                </div>
            </div>
            {actions && actions.length > 0 && (
                <div className="dashboard-section-actions">
                    {actions.map(({ label, onClick, variant = 'default' }) => (
                        <button
                            type="button"
                            key={label}
                            className={`dashboard-action-btn${variant === 'outline' ? ' outline' : ''}`}
                            onClick={onClick}
                        >
                            {label}
                        </button>
                    ))}
                </div>
            )}
        </div>
        <div className="dashboard-section-body">{children}</div>
    </section>
);

const SystemOverview = ({ data, formatCurrency, formatNumber, onRefresh, onNavigate }) => {
    if (!data) return null;

    const financialMetrics = [
        { label: 'Total Revenue', value: formatCurrency(data.totalRevenue || 0), tone: 'success' },
        { label: 'Total Orders', value: formatNumber(data.totalOrders || 0) },
        { label: 'Pending Orders', value: formatNumber(data.pendingOrders || 0), tone: 'warning' }
    ];

    const platformMetrics = [
        { label: 'Total Users', value: formatNumber(data.totalUsers || 0) },
        { label: 'Total Events', value: formatNumber(data.totalEvents || 0) },
        { label: 'Active Events', value: formatNumber(data.activeEvents || 0), tone: 'success' }
    ];

    const systemHealth = [
        { label: 'Server Status', value: data.serverStatus || 'Unknown', tone: 'success' },
        { label: 'Database', value: data.databaseStatus || 'Unknown', tone: 'success' },
        { label: 'Payments', value: data.paymentGatewayStatus || 'Unknown', tone: 'success' }
    ];

    return (
        <DashboardSection
            title="System Overview"
            icon={<Monitor size={18} />}
            gradient
            actions={[
                { label: 'Refresh', onClick: onRefresh, variant: 'outline' },
                { label: 'Details', onClick: onNavigate }
            ]}
        >
            <div className="system-overview-grid">
                <article className="overview-card">
                    <div className="overview-card-title">
                        <span role="img" aria-hidden>
                            <DollarSign size={16} />
                        </span>
                        <span>Financial Metrics</span>
                    </div>
                    <div className="metric-list">
                        {financialMetrics.map(({ label, value, tone }) => (
                            <div className="metric-item" key={label}>
                                <span className="metric-label">{label}</span>
                                <span className={`metric-value ${tone || ''}`}>{value}</span>
                            </div>
                        ))}
                    </div>
                </article>

                <article className="overview-card">
                    <div className="overview-card-title">
                        <span role="img" aria-hidden>
                            <Users size={16} />
                        </span>
                        <span>Platform Metrics</span>
                    </div>
                    <div className="metric-list">
                        {platformMetrics.map(({ label, value, tone }) => (
                            <div className="metric-item" key={label}>
                                <span className="metric-label">{label}</span>
                                <span className={`metric-value ${tone || ''}`}>{value}</span>
                            </div>
                        ))}
                    </div>
                </article>

                <article className="overview-card">
                    <div className="overview-card-title">
                        <span role="img" aria-hidden>
                            <Settings size={16} />
                        </span>
                        <span>System Health</span>
                    </div>
                    <div className="metric-list">
                        {systemHealth.map(({ label, value, tone }) => (
                            <div className="metric-item" key={label}>
                                <span className="metric-label">{label}</span>
                                <span className={`metric-value ${tone || ''}`}>{value}</span>
                            </div>
                        ))}
                    </div>
                </article>
            </div>
        </DashboardSection>
    );
};

const TopEvents = ({ events, formatCurrency }) => (
    <DashboardSection
        title="Top Performing Events"
        icon={<Trophy size={18} />}
        subtitle="Live insight into revenue and capacity"
    >
        <div className="top-events-grid">
            {!events || events.length === 0 ? (
                <div className="dashboard-empty-state">No active events to display</div>
            ) : (
                events.map((event) => (
                    <article className="event-card" key={event.id}>
                        <div className="event-header">
                            <div className="event-info">
                                <h3>{event.title}</h3>
                                <span>{event.date ? new Date(event.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'TBA'}</span>
                            </div>
                            <span className={`event-status ${event.status || 'draft'}`}>
                                {event.status === 'live' ? 'Live' : 'Upcoming'}
                            </span>
                        </div>
                        <div className="event-metrics">
                            <div className="event-metric">
                                <div className="value" style={{ color: event.color }}>{formatCurrency(event.revenue || 0)}</div>
                                <div className="label">Revenue</div>
                            </div>
                            <div className="event-metric">
                                <div className="value">{event.ticketsSold ?? '—'}</div>
                                <div className="label">Tickets Sold</div>
                            </div>
                            <div className="event-metric">
                                <div className="value">{event.capacity ?? '—'}</div>
                                <div className="label">Capacity</div>
                            </div>
                            <div className="event-metric">
                                <div className="value">
                                    {event.occupancy != null
                                        ? `${event.occupancy.toFixed(1)}%`
                                        : (event.capacity ? `${Math.round(((event.ticketsSold || 0) / event.capacity) * 100)}%` : '—')}
                                </div>
                                <div className="label">Sell-through</div>
                            </div>
                        </div>
                    </article>
                ))
            )}
        </div>
    </DashboardSection>
);

const QuickActions = ({ actions }) => (
    <DashboardSection
        title="Quick Actions"
        icon={<Zap size={18} />}
        subtitle="Jump directly into the most common workflows"
    >
        <div className="quick-actions-grid">
            {!actions || actions.length === 0 ? (
                <div className="dashboard-empty-state">No actions available</div>
            ) : (
                actions.map((action) => (
                    <button
                        type="button"
                        key={action.title}
                        className="quick-action-card"
                        onClick={action.action}
                    >
                        <div className="quick-action-icon" aria-hidden>
                            {action.icon}
                        </div>
                        <div className="quick-action-title">{action.title}</div>
                        <p className="quick-action-description">{action.description}</p>
                    </button>
                ))
            )}
        </div>
    </DashboardSection>
);

const ActivityFeed = ({ items }) => (
    <DashboardSection
        title="Recent Activity"
        icon={<ClipboardList size={18} />}
        subtitle="Important updates across the platform"
    >
        <div className="activity-feed">
            <div className="activity-list">
                {!items || items.length === 0 ? (
                    <div className="dashboard-empty-state">No recent activity</div>
                ) : (
                    items.map((item) => (
                        <div className="activity-item" key={item.id}>
                            <div className="activity-icon" aria-hidden>
                                {item.icon}
                            </div>
                            <div className="activity-details">
                                <h4>{item.title}</h4>
                                <p>{item.description}</p>
                                <span>{item.time}</span>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    </DashboardSection>
);

const DashboardPanel = ({
    capabilities,
    systemData,
    topEvents,
    quickActions,
    recentActivity,
    formatCurrency,
    formatNumber,
    loadDashboardData,
    navigate
}) => {
    return (
        <div className="admin-dashboard-grid">
            {capabilities?.sections?.includes('system') && systemData && (
                <SystemOverview
                    data={systemData}
                    formatCurrency={formatCurrency}
                    formatNumber={formatNumber}
                    onRefresh={loadDashboardData}
                    onNavigate={() => navigate('/dashboard/monitoring')}
                />
            )}

            {capabilities?.sections?.includes('events') && (
                <TopEvents events={topEvents} formatCurrency={formatCurrency} />
            )}

            {capabilities?.sections?.includes('actions') && quickActions && quickActions.length > 0 && (
                <QuickActions actions={quickActions} />
            )}

            {capabilities?.sections?.includes('activity') && (
                <ActivityFeed items={recentActivity} />
            )}
        </div>
    );
};

export default DashboardPanel;
